import { useEffect, useMemo, useState } from "react";
import * as Three from "three";
import confetti from "canvas-confetti";
import { ThreeCanvas } from "@/lib/3d/ThreeCanvas";
import { PromiseGLTFLoader } from "@/lib/3d/PromiseGLTFLoader";
import { Emblem, getGoldenThresholdForEmblemRarity } from "@/features/common/components/Emblem";
import { getColorForEmblemRarity } from "@/features/emblems/getColorForEmblemRarity";
import type { EmblemRarity } from "@/lib/domain/EmblemRarity";
import type { EmblemSetType } from "@/lib/domain/EmblemSet";
import { useRoll } from "@/features/emblems/api/useRoll";

export interface ModalEmblemsRollProps {
	set: EmblemSetType;
	visible: boolean;
	onCancel: () => void;
}

interface DrawArgs {
	scene: Three.Scene;
	camera: Three.Camera;
	renderer: Three.WebGLRenderer;
	tt: number;
	ot: number;
	chosenAt: number | null;
	gemCommon: Three.Group<Three.Object3DEventMap>;
	gemRare: Three.Group<Three.Object3DEventMap>;
	gemMajestic: Three.Group<Three.Object3DEventMap>;
	gemLegendary: Three.Group<Three.Object3DEventMap>;
	gemFabled: Three.Group<Three.Object3DEventMap>;
}

export const ModalEmblemsRoll = (props: ModalEmblemsRollProps): React.JSX.Element => {
	const {
		set,
		visible,
		onCancel
	} = props;

	const [gems, setGems] = useState<Three.Group<Three.Object3DEventMap>[] | null>(null);
	const roll = useRoll();
	const updateFunc = useMemo(() => updateRollScene(roll.data?.body.rarity), [roll.data]);
	const [emblemVisible, setEmblemVisible] = useState(false);

	useEffect(() => {
		const gltfLoader = new PromiseGLTFLoader();
		const promises = ["common", "rare", "majestic", "legendary", "fabled"].map(x => gltfLoader.load(`/gems/${x}.glb`));
		Promise.all(promises).then(models => {
			setGems(models.map(x => x.scene));
		});
	}, []);

	useEffect(() => {
		if (!visible || !gems) {
			return;
		}

		setTimeout(() => roll.mutate({ set }), 3500);
	}, [visible, gems]);

	useEffect(() => {
		if (!roll.isSuccess) {
			return;
		}

		setTimeout(() => confetti({
			particleCount: getParticleCountForRarity(roll.data.body.rarity),
			spread: 120,
			origin: { y: 0.5 },
			colors: getColorsForRarity(roll.data.body.rarity)
		}), 800);

		if (roll.data.body.count === getGoldenThresholdForEmblemRarity(roll.data.body.rarity)) {
			const goldenConfetti = () => confetti({
				particleCount: 100,
				spread: 100,
				origin: { y: 0.5 },
				colors: ["#ffae00"]
			});

			setTimeout(goldenConfetti, 800);
			setTimeout(goldenConfetti, 1000);
			setTimeout(goldenConfetti, 1200);
			setTimeout(goldenConfetti, 1400);
			setTimeout(goldenConfetti, 1600);
			setTimeout(goldenConfetti, 1800);
		}

		setTimeout(() => setEmblemVisible(true), 1200);
		setTimeout(() => setEmblemVisible(false), 4000);

		setTimeout(() => {
			onCancel();
			roll.reset();
		}, 6000);
	}, [roll.isSuccess]);

	if (!visible || !gems) {
		return <></>;
	}

	const [canvasWidth, canvasHeight] = [window.innerWidth, Math.min(window.innerWidth, window.innerHeight)];

	return (
		<div className="fixed inset-0 z-50 select-none">
			<div
				className={`absolute inset-0 flex flex-col items-center justify-center gap-4 transition-opacity duration-2000 z-10 pointer-events-none ${emblemVisible ? "opacity-100" : "opacity-0"}`}>
				<div className="w-40 h-40">
					{
						roll.data && (
							<Emblem
								{...roll.data.body}
								count={roll.data.body.count > getGoldenThresholdForEmblemRarity(roll.data.body.rarity) ? 1 : roll.data.body.count}
							/>
						)
					}
				</div>
				{
					roll.data && (
						<div className="flex flex-col items-center gap-2">
							<div className="text-white text-xl font-bold">{roll.data.body.name}</div>
							<div
								className="text-white text-sm font-semibold px-3 py-1 rounded-full"
								style={{ backgroundColor: getColorForEmblemRarity(roll.data.body.rarity) }}>
								{roll.data.body.rarity}
							</div>
						</div>
					)
				}
			</div>

			<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
				<ThreeCanvas
					width={canvasWidth}
					height={canvasHeight}
					setupArgs={gems}
					setupFunc={setupRollScene}
					updateFunc={updateFunc}
				/>
			</div>
		</div>
	);
};

const setupRollScene = (canvas: HTMLCanvasElement, ctx: WebGLRenderingContext, setupGems: Three.Group<Three.Object3DEventMap>[]): DrawArgs => {
	const scene = new Three.Scene();
	const camera = new Three.PerspectiveCamera(75, canvas.width / canvas.height, 0.1, 1000);

	camera.position.z = 0;
	camera.lookAt(new Three.Vector3(0, 0, 0));

	const ambientLight = new Three.AmbientLight(0xFFFFFF, 3);
	scene.add(ambientLight);

	const directionalLight = new Three.DirectionalLight(0xffffff, 6);
	directionalLight.position.set(5, 5, 5).normalize();
	scene.add(directionalLight);

	const fillLight = new Three.DirectionalLight(0xffffff, 4);
	fillLight.position.set(-5, -3, -5).normalize();
	scene.add(fillLight);

	const renderer = new Three.WebGLRenderer({
		canvas: canvas,
		context: ctx,
		antialias: true
	});
	renderer.setSize(canvas.width, canvas.height);

	setupGems.forEach(x => scene.add(x));

	return {
		scene,
		camera,
		renderer,
		tt: 0,
		ot: new Date().getTime(),
		chosenAt: 0,
		gemCommon: setupGems[0],
		gemRare: setupGems[1],
		gemMajestic: setupGems[2],
		gemLegendary: setupGems[3],
		gemFabled: setupGems[4]
	};
};

const GEM_SCALE = 8.5;

const updateRollScene = (rarity: EmblemRarity | undefined) => (args: DrawArgs, dt: number) => {
	args.tt += dt;

	if (rarity && !args.chosenAt) {
		args.chosenAt = args.tt;
	}

	if (args.tt > 1500) {
		args.camera.position.z = 32;
	} else {
		args.camera.position.z = Math.sin((args.tt / 1500) * Math.PI / 2) * 32;
	}

	const ct = args.tt - (args.chosenAt || args.tt);
	const chosenDistance = 12 - Math.sin(Math.min(ct / 1500, 1) * Math.PI / 2) * 12;

	// Common
	const commonDistance = rarity && rarity === "Common" ? chosenDistance : 12;
	args.gemCommon.position.x = Math.sin(((args.tt + args.ot) / 1000) + 0 * Math.PI * 2 / 5) * commonDistance;
	args.gemCommon.position.y = Math.cos(((args.tt + args.ot) / 1000) + 0 * Math.PI * 2 / 5) * commonDistance;
	args.gemCommon.rotation.x = Math.sin((args.tt / 3750) + 0 * Math.PI * 2 / 5) * Math.PI * 2;
	args.gemCommon.rotation.y = Math.cos((args.tt / 3750) + 0 * Math.PI * 2 / 5) * Math.PI * 2;

	if (rarity && rarity !== "Common") {
		args.gemCommon.scale.setScalar(GEM_SCALE * (1 - Math.min(ct / 1500, 1)));
	} else if (rarity === "Common") {
		args.gemCommon.scale.setScalar(GEM_SCALE * Math.max(1 - (Math.max(ct - 3000, 0) / 1000), 0));
	} else {
		args.gemCommon.scale.setScalar(GEM_SCALE);
	}

	// Rare
	const rareDistance = rarity && rarity === "Rare" ? chosenDistance : 12;
	args.gemRare.position.x = Math.sin(((args.tt + args.ot) / 1000) + 1 * Math.PI * 2 / 5) * rareDistance;
	args.gemRare.position.y = Math.cos(((args.tt + args.ot) / 1000) + 1 * Math.PI * 2 / 5) * rareDistance;
	args.gemRare.rotation.x = Math.sin((args.tt / 3750) + 3 * Math.PI * 2 / 5) * Math.PI * 2;
	args.gemRare.rotation.y = Math.cos((args.tt / 3750) + 3 * Math.PI * 2 / 5) * Math.PI * 2;

	if (rarity && rarity !== "Rare") {
		args.gemRare.scale.setScalar(GEM_SCALE * (1 - Math.min(ct / 1500, 1)));
	} else if (rarity === "Rare") {
		args.gemRare.scale.setScalar(GEM_SCALE * Math.max(1 - (Math.max(ct - 3000, 0) / 1000), 0));
	} else {
		args.gemRare.scale.setScalar(GEM_SCALE);
	}

	// Majestic
	const majesticDistance = rarity && rarity === "Majestic" ? chosenDistance : 12;
	args.gemMajestic.position.x = Math.sin(((args.tt + args.ot) / 1000) + 2 * Math.PI * 2 / 5) * majesticDistance;
	args.gemMajestic.position.y = Math.cos(((args.tt + args.ot) / 1000) + 2 * Math.PI * 2 / 5) * majesticDistance;
	args.gemMajestic.rotation.x = Math.sin((args.tt / 3750) + 2 * Math.PI * 2 / 5) * Math.PI * 2;
	args.gemMajestic.rotation.y = Math.cos((args.tt / 3750) + 2 * Math.PI * 2 / 5) * Math.PI * 2;

	if (rarity && rarity !== "Majestic") {
		args.gemMajestic.scale.setScalar(GEM_SCALE * (1 - Math.min(ct / 1500, 1)));
	} else if (rarity === "Majestic") {
		args.gemMajestic.scale.setScalar(GEM_SCALE * Math.max(1 - (Math.max(ct - 3000, 0) / 1000), 0));
	} else {
		args.gemMajestic.scale.setScalar(GEM_SCALE);
	}

	// Legendary
	const legendaryDistance = rarity && rarity === "Legendary" ? chosenDistance : 12;
	args.gemLegendary.position.x = Math.sin(((args.tt + args.ot) / 1000) + 3 * Math.PI * 2 / 5) * legendaryDistance;
	args.gemLegendary.position.y = Math.cos(((args.tt + args.ot) / 1000) + 3 * Math.PI * 2 / 5) * legendaryDistance;
	args.gemLegendary.rotation.x = Math.sin((args.tt / 3750) + 3 * Math.PI * 2 / 5) * Math.PI * 2;
	args.gemLegendary.rotation.y = Math.cos((args.tt / 3750) + 3 * Math.PI * 2 / 5) * Math.PI * 2;

	if (rarity && rarity !== "Legendary") {
		args.gemLegendary.scale.setScalar(GEM_SCALE * (1 - Math.min(ct / 1500, 1)));
	} else if (rarity === "Legendary") {
		args.gemLegendary.scale.setScalar(GEM_SCALE * Math.max(1 - (Math.max(ct - 3000, 0) / 1000), 0));
	} else {
		args.gemLegendary.scale.setScalar(GEM_SCALE);
	}

	// Fabled
	const fabledDistance = rarity && rarity === "Fabled" ? chosenDistance : 12;
	args.gemFabled.position.x = Math.sin(((args.tt + args.ot) / 1000) + 4 * Math.PI * 2 / 5) * fabledDistance;
	args.gemFabled.position.y = Math.cos(((args.tt + args.ot) / 1000) + 4 * Math.PI * 2 / 5) * fabledDistance;
	args.gemFabled.rotation.x = Math.sin((args.tt / 3750) + 5 * Math.PI * 2 / 5) * Math.PI * 2;
	args.gemFabled.rotation.y = Math.cos((args.tt / 3750) + 5 * Math.PI * 2 / 5) * Math.PI * 2;

	if (rarity && rarity !== "Fabled") {
		args.gemFabled.scale.setScalar(GEM_SCALE * (1 - Math.min(ct / 1500, 1)));
	} else if (rarity === "Fabled") {
		args.gemFabled.scale.setScalar(GEM_SCALE * Math.max(1 - (Math.max(ct - 3000, 0) / 1000), 0));
	} else {
		args.gemFabled.scale.setScalar(GEM_SCALE);
	}

	args.renderer.render(args.scene, args.camera);
};

const getColorsForRarity = (rarity: EmblemRarity): string[] => {
	switch (rarity) {
	case "Rare":
		return ["#EEEEEE", "#124AED", "#459AFF"];
	case "Majestic":
		return ["#ED2A12", "#FF5A45", "#FFEEDD"];
	case "Legendary":
		return ["#FFDD12", "#DD9945", "#FFEEDD", "#ED2A12"];
	case "Fabled":
		return ["#5AAA3E", "#67AA4F", "#EEFFDD"];
	default:
		return ["#FFFFFF", "#CCCCCC", "#EEEEEE", "#DDDDDD", "#666666"];
	}
};

const getParticleCountForRarity = (rarity: EmblemRarity): number => {
	switch (rarity) {
	case "Rare":
		return 200;
	case "Majestic":
		return 250;
	case "Legendary":
		return 300;
	case "Fabled":
		return 600;
	default:
		return 100;
	}
};
